import { useState, useEffect } from "react";
import { Shield, Check, Loader2, ArrowRight, ArrowLeft, Lock, CalendarCheck, IdCard, CreditCard, MailCheck, LogIn, Clock, BookOpen, UserPlus, X } from "lucide-react";
import { EquipementRequisBox } from "../components/equipment/EquipementRequisBox";

const API = process.env.REACT_APP_BACKEND_URL;

// Descriptions courtes par programme (le reste vient de l'API /inscriptions/programmes)
const PROG_DESC = {
  parent_enfant: "Un parent avec son enfant (3-6 ans). Découverte, discipline et coordination.",
  dynamo: "Discipline, confiance et progression martiale pour les jeunes.",
  junior: "Cadre structuré avec objectifs et évolution pour ados.",
  adultes: "Commencer ou reprendre dans un environnement encadré et motivant.",
  kickboxing_muaythai: "Technique, conditionnement et progression. Mardi & jeudi 18 h.",
  jiu_jitsu_can_ryu: "Self-défense et jiu-jitsu traditionnel japonais.",
  bjj: "Jiu-jitsu au sol, sparring et progression par ceintures.",
};

// Les 7 étapes visibles du parcours (« Comment ça fonctionne »)
const PARCOURS = [
  { icon: IdCard, label: "Choisir mon programme" },
  { icon: CalendarCheck, label: "Remplir mon inscription" },
  { icon: Check, label: "Vérifier et confirmer" },
  { icon: CreditCard, label: "Finaliser le paiement (avec l'équipe)" },
  { icon: MailCheck, label: "Recevoir ma confirmation" },
  { icon: LogIn, label: "Me connecter à mon espace membre" },
  { icon: BookOpen, label: "Voir mon horaire et mon carnet" },
];

export default function InscriptionPublic() {
  const [programmes, setProgrammes] = useState([]);
  const [step, setStep] = useState("landing"); // landing | infos | validation | done
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [consents, setConsents] = useState({ exact: false, conditions: false, contact: false, photo: false });
  const [form, setForm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      programme: "", type_inscription: "", discipline: "muay_thai_kickboxing", frequence: "", niveau: "",
      full_name: "", email: "", phone: "", date_of_birth: "", niveau_actuel: "",
      is_minor: false, parent_name: "", parent_email: "", parent_phone: "",
      antecedents_medicaux: "", objectifs: "", consent_text: false,
      referer: "", source: params.get("source") || (params.get("promo") ? "promo_" + params.get("promo") : ""), members: [],
    };
  });

  useEffect(() => {
    fetch(`${API}/api/inscriptions/programmes`).then((r) => r.json())
      .then((d) => {
        const list = d.programmes || [];
        setProgrammes(list);
        const q = new URLSearchParams(window.location.search);
        const pre = list.find((x) => x.key === q.get("programme"));
        if (pre) {
          const singleNiveau = (pre.niveaux || []).length === 1 ? pre.niveaux[0].key : "";
          const isParent = pre.type === "parent_enfant";
          const nom = q.get("nom") || q.get("name") || "", courriel = q.get("courriel") || q.get("email") || "";
          setForm((f) => ({ ...f, programme: pre.key, discipline: pre.discipline, type_inscription: pre.type,
            is_minor: isParent || pre.type === "enfant", niveau: singleNiveau,
            ...(isParent ? { parent_name: nom || f.parent_name, parent_email: courriel || f.parent_email }
                         : { full_name: nom || f.full_name, email: courriel || f.email }) }));
          setStep("infos");
        }
      }).catch(() => setProgrammes([]));
    const p = new URLSearchParams(window.location.search).get("paiement");
    if (p === "success") { setResult({ paid: true }); setStep("done"); }
    else if (p === "cancel") { setError("Paiement annulé. Ton inscription a bien été enregistrée — l'équipe te contactera pour finaliser."); setStep("done"); setResult({ paid: false }); }
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedProg = programmes.find((p) => p.key === form.programme);
  const niveaux = selectedProg?.niveaux || [];
  const selectedNiveau = niveaux.find((n) => n.key === form.niveau);
  const freqs = selectedNiveau?.freqs || [];
  const selectedTarif = freqs.find((t) => t.key === form.frequence);
  const minTarif = (p) => (p.niveaux || []).reduce((m, n) => Math.min(m, ...n.freqs.map((f) => f.amount)), Infinity);

  // --- Rabais famille : quote du foyer (membre principal + membres) ---
  const [quote, setQuote] = useState(null);
  const buildHouseholdItems = () => {
    const items = [{ full_name: form.full_name, programme: form.programme, niveau: form.niveau, frequence: form.frequence }];
    (form.members || []).forEach((m) => {
      if (m.programme && m.frequence && (m.niveau || (programmes.find((p) => p.key === m.programme)?.niveaux?.length === 1)))
        items.push({ full_name: m.full_name, programme: m.programme, niveau: m.niveau, frequence: m.frequence });
    });
    return items;
  };
  const refreshQuote = async (items) => {
    try {
      const r = await fetch(`${API}/api/inscriptions/quote`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items }),
      });
      const d = await r.json();
      if (r.ok) setQuote(d);
    } catch (_) { /* silencieux */ }
  };
  useEffect(() => {
    if (step === "validation") refreshQuote(buildHouseholdItems());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, JSON.stringify(form.members), form.programme, form.niveau, form.frequence]);

  const addMember = () => setForm((f) => ({ ...f, members: [...(f.members || []), { full_name: "", programme: "", niveau: "", frequence: "" }] }));
  const removeMember = (idx) => setForm((f) => ({ ...f, members: f.members.filter((_, i) => i !== idx) }));
  const updateMember = (idx, k, v) => setForm((f) => {
    const members = [...f.members];
    members[idx] = { ...members[idx], [k]: v };
    if (k === "programme") {
      const prog = programmes.find((p) => p.key === v);
      members[idx].niveau = prog?.niveaux?.length === 1 ? prog.niveaux[0].key : "";
      members[idx].frequence = "";
    }
    if (k === "niveau") members[idx].frequence = "";
    return { ...f, members };
  });

  const chooseProgram = (p) => {
    const singleNiveau = (p.niveaux || []).length === 1 ? p.niveaux[0].key : "";
    setForm((f) => ({
      ...f, programme: p.key, discipline: p.discipline, type_inscription: p.type,
      is_minor: p.type === "parent_enfant" || p.type === "enfant",
      niveau: singleNiveau, frequence: "",
    }));
    setStep("infos");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isParent = form.type_inscription === "parent_enfant" || form.type_inscription === "enfant";
  const progLabel = programmes.find((p) => p.key === form.programme)?.label || "";

  const goValidation = (e) => {
    e.preventDefault();
    setError(null);
    if (!form.full_name.trim()) { setError(isParent ? "Le nom de l'enfant est requis." : "Ton nom complet est requis."); return; }
    if (isParent && !form.parent_name.trim()) { setError("Le nom du parent est requis."); return; }
    if (niveaux.length > 1 && !form.niveau) { setError("Choisis ton niveau pour continuer."); return; }
    if (!form.frequence) { setError("Choisis une formule (fréquence) pour continuer."); return; }
    setStep("validation");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setError(null);
    if (!(consents.exact && consents.conditions && consents.contact)) {
      setError("Merci de cocher les trois confirmations pour continuer."); return;
    }
    setSubmitting(true);
    try {
      const body = { ...form, consent_text: true, photo_consent: !!consents.photo };
      ["email", "parent_email"].forEach((k) => { if (body[k] === "") delete body[k]; });
      const r = await fetch(`${API}/api/inscriptions`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        let msg = "Erreur";
        if (data?.detail) msg = typeof data.detail === "string" ? data.detail
          : Array.isArray(data.detail) ? data.detail.map((d) => d.msg || "champ invalide").join(" · ") : msg;
        throw new Error(msg);
      }
      setResult(data);
      // Redirection vers le paiement Stripe (abonnement aux 4 semaines)
      try {
        const cr = await fetch(`${API}/api/inscriptions/checkout`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ inscription_id: data.id, origin_url: window.location.origin }),
        });
        const cd = await cr.json().catch(() => null);
        if (cr.ok && cd?.url) { window.location.href = cd.url; return; }
      } catch (_) { /* le lead est capturé ; on affiche la confirmation */ }
      setStep("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  };

  const stepNum = step === "infos" ? 2 : step === "validation" ? 3 : 1;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* HEADER */}
      <header className="border-b border-impact-gold/40 px-6 py-6 bg-gradient-to-r from-black via-zinc-900 to-black">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="ticker text-impact-gold text-sm">— INSTITUT IMPACT ÉLITE MARTIAL</div>
          <a href="/membres" className="text-xs text-white/60 hover:text-impact-gold inline-flex items-center gap-1" data-testid="link-espace-membre"><LogIn size={13}/> Espace membre</a>
        </div>
      </header>

      {step !== "landing" && step !== "done" && (
        <div className="max-w-2xl mx-auto px-6 pt-6" data-testid="step-progress">
          <div className="flex items-center justify-between text-xs text-white/60 mb-2">
            <button onClick={() => setStep(step === "validation" ? "infos" : "landing")} className="inline-flex items-center gap-1 hover:text-impact-gold" data-testid="btn-back"><ArrowLeft size={14}/> Retour</button>
            <span>Étape {stepNum} sur 3</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-impact-gold transition-all" style={{ width: `${(stepNum / 3) * 100}%` }}/>
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-2xl mx-auto px-6 pt-4">
          <div className="border border-impact-red bg-impact-red/10 text-impact-red px-4 py-3 text-sm rounded" data-testid="error-banner">{error}</div>
        </div>
      )}

      {/* ===== LANDING ===== */}
      {step === "landing" && (
        <div data-testid="inscription-landing">
          {new URLSearchParams(window.location.search).get("promo") === "rentree" && (
            <div data-testid="promo-rentree-banner" className="bg-gradient-to-r from-[#C9A961] to-[#e5c877] text-black text-center py-3 px-4 font-bold text-sm sm:text-base">
              🎁 Promo de rentrée — <span className="underline">-25 % sur tes frais d'inscription</span> (35&nbsp;$ → <strong>26,25&nbsp;$</strong>). Le rabais s'applique <strong>automatiquement au paiement</strong>.
            </div>
          )}
          <section className="max-w-4xl mx-auto px-6 pt-14 pb-8 text-center">
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">Bienvenue à l'Institut Impact Élite Martial</h1>
            <p className="text-white/70 mt-5 max-w-2xl mx-auto text-base">Choisissez votre programme, complétez votre inscription et recevez vos accès en quelques étapes.</p>
            <a href="#programmes" className="mt-8 inline-flex items-center gap-2 bg-impact-gold text-black font-display font-black text-lg px-8 py-4 hover:bg-white transition-colors rounded" data-testid="cta-commencer">
              Commencer l'inscription <ArrowRight size={20}/>
            </a>
          </section>

          {/* 7 étapes visibles */}
          <section className="max-w-4xl mx-auto px-6 py-8">
            <h2 className="ticker text-impact-gold text-center mb-6">COMMENT ÇA FONCTIONNE</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {PARCOURS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="flex items-center gap-3 border border-white/10 rounded-lg p-3 bg-white/5">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-impact-gold/20 text-impact-gold flex items-center justify-center font-bold text-sm">{i + 1}</span>
                    <div className="flex items-center gap-2 text-sm text-white/80"><Icon size={15} className="text-impact-gold shrink-0"/> {s.label}</div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Cartes programmes */}
          <section id="programmes" className="max-w-4xl mx-auto px-6 py-8 scroll-mt-6">
            <h2 className="font-display font-black text-2xl text-center mb-2">Choisissez votre programme</h2>
            <p className="text-white/60 text-center text-sm mb-4">Sélectionnez le programme qui correspond le mieux à votre âge, votre niveau ou votre objectif.</p>
            <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="inscription-equipement-links">
              {[["pcv", "PCV"], ["pcn", "PCN"], ["master", "Master Club"]].map(([k, l]) => (
                <a key={k} href={`/equipement/${k}`} target="_blank" rel="noreferrer" className="text-xs text-impact-gold underline" data-testid={`inscription-equipement-${k}`}>🥋 Équipement {l} : tailles, photos et commande groupée →</a>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {programmes.map((p) => (
                <div key={p.key} className="border border-impact-gold/30 rounded-xl p-5 bg-white/5 hover:border-impact-gold transition-colors flex flex-col" data-testid={`prog-card-${p.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display font-bold text-lg">{p.label}</h3>
                    <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-impact-gold/15 text-impact-gold">{p.age}</span>
                  </div>
                  <p className="text-white/60 text-sm mt-2 flex-1">{PROG_DESC[p.key] || ""}</p>
                  {p.niveaux?.length > 0 && <div className="text-impact-gold text-sm font-semibold mt-2">À partir de {minTarif(p).toFixed(2)}$ / 4 sem. <span className="text-white/40 font-normal">(taxes incl.)</span></div>}
                  <button onClick={() => chooseProgram(p)} data-testid={`choose-${p.key}`}
                    className="mt-4 inline-flex items-center justify-center gap-2 bg-impact-gold text-black font-display font-bold text-base py-3 rounded-lg shadow-[0_0_22px_rgba(212,160,23,0.4)] hover:bg-white hover:shadow-[0_0_30px_rgba(212,160,23,0.65)] transition-all active:scale-[0.98]">
                    Je choisis ce programme <ArrowRight size={18}/>
                  </button>
                </div>
              ))}
              {/* PowerKick Her — programme dédié (préinscription directe) */}
              <a href="/powerkick-her/preinscription" data-testid="prog-card-powerkick-her"
                className="border border-pink-400/40 rounded-xl p-5 bg-gradient-to-br from-pink-500/15 to-white/5 hover:border-pink-400 transition-colors flex flex-col group">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-lg">PowerKick Her 🥊</h3>
                  <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-pink-400/20 text-pink-300">Femmes</span>
                </div>
                <p className="text-white/60 text-sm mt-2 flex-1">Kickboxing 100 % féminin : cardio, confiance et autodéfense dans un groupe motivant. Préinscription dédiée.</p>
                <span data-testid="choose-powerkick-her"
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-pink-500 text-white font-display font-bold text-base py-3 rounded-lg shadow-[0_0_22px_rgba(236,72,153,0.45)] group-hover:bg-pink-400 transition-all">
                  Je choisis ce programme <ArrowRight size={18}/>
                </span>
              </a>
            </div>
          </section>

          {/* Bloc confiance */}
          <section className="max-w-4xl mx-auto px-6 py-10">
            <div className="border border-white/10 rounded-xl p-6 bg-white/5">
              <h2 className="font-display font-bold text-lg text-center mb-4">Une inscription simple et sécurisée</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm text-white/70">
                <div className="flex flex-col items-center gap-1"><Lock size={20} className="text-impact-gold"/> Paiement sécurisé</div>
                <div className="flex flex-col items-center gap-1"><MailCheck size={20} className="text-impact-gold"/> Confirmation envoyée</div>
                <div className="flex flex-col items-center gap-1"><LogIn size={20} className="text-impact-gold"/> Accès espace membre</div>
                <div className="flex flex-col items-center gap-1"><Clock size={20} className="text-impact-gold"/> Horaire centralisé</div>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ===== ÉTAPE 2 : INFORMATIONS ===== */}
      {step === "infos" && (
        <form onSubmit={goValidation} className="max-w-2xl mx-auto px-6 py-8 space-y-6" data-testid="inscription-form">
          <div className="border border-impact-gold/30 bg-impact-gold/5 rounded-lg px-4 py-3 text-sm">
            <span className="text-white/60">Programme choisi :</span> <span className="font-semibold text-impact-gold">{progLabel}</span>
          </div>
          <h2 className="font-display font-black text-2xl">{isParent ? "Informations du parent et de l'enfant" : "Vos informations"}</h2>

          {isParent && (
            <Section title="PARENT / TUTEUR">
              <Field label="Nom complet du parent *">
                <input data-testid="f-parent-name" required value={form.parent_name} onChange={(e) => update("parent_name", e.target.value)} className="input"/>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Courriel du parent">
                  <input data-testid="f-parent-email" type="email" value={form.parent_email} onChange={(e) => update("parent_email", e.target.value)} className="input"/>
                </Field>
                <Field label="Téléphone du parent">
                  <input data-testid="f-parent-phone" value={form.parent_phone} onChange={(e) => update("parent_phone", e.target.value)} className="input"/>
                </Field>
              </div>
            </Section>
          )}

          <Section title={isParent ? "ENFANT" : "TOI"}>
            <Field label={isParent ? "Nom complet de l'enfant *" : "Nom complet *"}>
              <input data-testid="f-name" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="input"/>
            </Field>
            {!isParent && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Courriel">
                  <input data-testid="f-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="input"/>
                </Field>
                <Field label="Téléphone">
                  <input data-testid="f-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input"/>
                </Field>
              </div>
            )}
            <Field label={isParent ? "Date de naissance de l'enfant" : "Date de naissance"}>
              <input data-testid="f-dob" type="date" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} className="input"/>
            </Field>
            {!isParent && (
              <Field label="As-tu déjà pratiqué ? Ceinture / niveau actuel ?">
                <input data-testid="f-niveau" placeholder="ex : débutant, ceinture jaune, 6 mois de boxe…" value={form.niveau_actuel} onChange={(e) => update("niveau_actuel", e.target.value)} className="input"/>
              </Field>
            )}
          </Section>

          <Section title="SÉCURITÉ / SANTÉ">
            <Field label="Allergies, condition ou blessure à signaler">
              <textarea data-testid="f-antecedents" rows={2} placeholder="Laisse vide si rien" value={form.antecedents_medicaux} onChange={(e) => update("antecedents_medicaux", e.target.value)} className="input"/>
            </Field>
            {!isParent && (
              <Field label="Tes objectifs">
                <textarea data-testid="f-objectifs" rows={2} placeholder="Perte de poids · compétition · self-défense · discipline…" value={form.objectifs} onChange={(e) => update("objectifs", e.target.value)} className="input"/>
              </Field>
            )}
          </Section>

          <Section title="COMMENT NOUS AVEZ-VOUS CONNUS ? (optionnel)">
            <input data-testid="f-referer" placeholder="Bouche-à-oreille / Google / Facebook / Ami…" value={form.referer} onChange={(e) => update("referer", e.target.value)} className="input"/>
          </Section>

          {selectedProg && <EquipementRequisBox program={selectedProg.key} />}

          {niveaux.length > 1 && (
            <Section title="NIVEAU">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {niveaux.map((n) => (
                  <button type="button" key={n.key} onClick={() => { update("niveau", n.key); update("frequence", ""); }} data-testid={`niveau-${n.key}`}
                    className={`px-3 py-2.5 rounded border text-sm text-center transition-colors ${form.niveau === n.key ? "border-impact-gold bg-impact-gold/10 text-white" : "border-white/15 hover:border-white/40 text-white/80"}`}>
                    {n.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-white/40 mt-1">Choisis selon ta ceinture / expérience actuelle. En cas de doute, choisis Débutant — l'équipe ajuste au besoin.</p>
            </Section>
          )}

          {(form.niveau || niveaux.length === 1) && (
          <Section title="FORMULE (au 4 semaines · taxes incluses)">
            <div className="grid grid-cols-1 gap-2">
              {freqs.map((t) => (
                <button type="button" key={t.key} onClick={() => update("frequence", t.key)} data-testid={`freq-${t.key}`}
                  className={`flex items-center justify-between px-4 py-3 rounded border text-left transition-colors ${form.frequence === t.key ? "border-impact-gold bg-impact-gold/10" : "border-white/15 hover:border-white/40"}`}>
                  <span className="text-sm text-white/85 flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${form.frequence === t.key ? "border-impact-gold" : "border-white/40"}`}>{form.frequence === t.key && <span className="w-2 h-2 rounded-full bg-impact-gold"/>}</span>
                    {t.label}
                  </span>
                  <span className="font-display font-bold text-impact-gold">{t.amount.toFixed(2)}$</span>
                </button>
              ))}
            </div>
          </Section>
          )}

          <button type="submit" data-testid="btn-continue-validation"
            className="w-full bg-impact-gold text-black font-display font-black text-lg py-4 hover:bg-white transition-colors flex items-center justify-center gap-3 rounded">
            Continuer <ArrowRight size={20}/>
          </button>
        </form>
      )}

      {/* ===== ÉTAPE 3 : VALIDATION ===== */}
      {step === "validation" && (
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-6" data-testid="inscription-validation">
          <h2 className="font-display font-black text-2xl">Vérification et confirmation</h2>
          <div className="border border-white/10 rounded-xl p-5 bg-white/5 space-y-2 text-sm" data-testid="recap">
            <Recap k="Programme" v={progLabel}/>
            <Recap k="Type d'inscription" v={isParent ? "Parent-enfant" : "Adulte"}/>
            <Recap k={isParent ? "Enfant" : "Membre"} v={form.full_name}/>
            {isParent && <Recap k="Parent" v={form.parent_name}/>}
            <Recap k="Courriel" v={form.email || form.parent_email || "—"}/>
            {selectedNiveau && niveaux.length > 1 && <Recap k="Niveau" v={selectedNiveau.label}/>}
            {selectedTarif && <Recap k="Formule" v={`${selectedTarif.label} — ${selectedTarif.amount.toFixed(2)}$ / 4 sem.`}/>}
            <Recap k="Paiement" v="Abonnement sécurisé Stripe (taxes incluses)"/>
          </div>

          {/* ===== RABAIS FAMILLE ===== */}
          <div className="border border-impact-gold/30 rounded-xl p-5 bg-impact-gold/5" data-testid="family-section">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="font-display font-bold text-lg">Ajouter des membres de ma famille</h3>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-impact-gold/20 text-impact-gold">Rabais famille</span>
            </div>
            <p className="text-white/60 text-sm mb-4">
              Inscrivez plusieurs personnes (ou plusieurs disciplines) dans le même foyer et économisez :
              <span className="text-impact-gold font-semibold"> 3<sup>e</sup> abonnement −10 %, 4<sup>e</sup> et suivants −15 %</span> (appliqué automatiquement aux abonnements les moins chers).
            </p>

            {(form.members || []).map((m, idx) => {
              const mProg = programmes.find((p) => p.key === m.programme);
              const mNiveaux = mProg?.niveaux || [];
              const mFreqs = (mNiveaux.find((n) => n.key === m.niveau)?.freqs) || [];
              return (
                <div key={idx} className="border border-white/15 rounded-lg p-4 mb-3 space-y-3 bg-black/30" data-testid={`member-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="ticker text-xs text-impact-gold">MEMBRE {idx + 2}</span>
                    <button type="button" onClick={() => removeMember(idx)} data-testid={`member-remove-${idx}`} className="text-white/50 hover:text-impact-red inline-flex items-center gap-1 text-xs"><X size={13}/> Retirer</button>
                  </div>
                  <input data-testid={`member-name-${idx}`} placeholder="Nom complet du membre" value={m.full_name} onChange={(e) => updateMember(idx, "full_name", e.target.value)} className="input"/>
                  <select data-testid={`member-prog-${idx}`} value={m.programme} onChange={(e) => updateMember(idx, "programme", e.target.value)} className="input">
                    <option value="">— Choisir un programme —</option>
                    {programmes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                  {mNiveaux.length > 1 && (
                    <select data-testid={`member-niveau-${idx}`} value={m.niveau} onChange={(e) => updateMember(idx, "niveau", e.target.value)} className="input">
                      <option value="">— Choisir un niveau —</option>
                      {mNiveaux.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
                    </select>
                  )}
                  {(m.niveau || mNiveaux.length === 1) && m.programme && (
                    <select data-testid={`member-freq-${idx}`} value={m.frequence} onChange={(e) => updateMember(idx, "frequence", e.target.value)} className="input">
                      <option value="">— Choisir une formule —</option>
                      {mFreqs.map((t) => <option key={t.key} value={t.key}>{t.label} — {t.amount.toFixed(2)}$</option>)}
                    </select>
                  )}
                </div>
              );
            })}

            <button type="button" onClick={addMember} data-testid="add-member-btn" className="w-full border border-dashed border-impact-gold/50 text-impact-gold rounded-lg py-2.5 text-sm hover:bg-impact-gold/10 transition-colors inline-flex items-center justify-center gap-2">
              <UserPlus size={15}/> Ajouter un membre / une discipline
            </button>

            {quote && quote.count > 1 && (
              <div className="mt-4 border-t border-white/10 pt-4 space-y-1.5" data-testid="household-breakdown">
                {quote.items.filter((p) => p.valid).map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-white/70">{p.full_name || p.prog_label} <span className="text-white/40">· {p.prog_label}</span></span>
                    <span className="text-white/90">
                      {p.discount_pct > 0 ? (
                        <><span className="line-through text-white/40 mr-2">{p.base_amount.toFixed(2)}$</span><span className="text-impact-gold font-semibold">{p.final_amount.toFixed(2)}$ (−{p.discount_pct}%)</span></>
                      ) : (<span>{p.final_amount.toFixed(2)}$</span>)}
                    </span>
                  </div>
                ))}
                {quote.savings > 0 && (
                  <div className="flex justify-between text-sm text-impact-gold" data-testid="household-savings"><span>Économie famille</span><span>−{quote.savings.toFixed(2)}$ / 4 sem.</span></div>
                )}
                <div className="flex justify-between font-display font-bold text-lg pt-1"><span>Total foyer</span><span className="text-impact-gold" data-testid="household-total">{quote.total_final.toFixed(2)}$ / 4 sem.</span></div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Consent id="c-exact" checked={consents.exact} onChange={(v) => setConsents((c) => ({ ...c, exact: v }))}>
              J'ai vérifié que mes informations sont exactes.
            </Consent>
            <Consent id="c-cond" checked={consents.conditions} onChange={(v) => setConsents((c) => ({ ...c, conditions: v }))}>
              J'accepte les conditions de l'inscription.
            </Consent>
            <Consent id="c-contact" checked={consents.contact} onChange={(v) => setConsents((c) => ({ ...c, contact: v }))}>
              J'accepte d'être contacté concernant mon inscription et mes informations de cours.
            </Consent>
            <Consent id="c-photo" checked={consents.photo} onChange={(v) => setConsents((c) => ({ ...c, photo: v }))}>
              <span className="font-semibold">Optionnel —</span> j'autorise l'Institut Impact Élite Martial à utiliser des photos et vidéos prises au dojo (de moi ou de mon enfant) pour présenter ses programmes sur ses réseaux sociaux, son site web et ses imprimés, sans rémunération et sans nom de famille. Je peux retirer cet accord en tout temps par courriel.
            </Consent>
          </div>

          <button onClick={submit} disabled={submitting} data-testid="btn-submit-inscription"
            className="w-full bg-impact-gold text-black font-display font-black text-lg py-4 hover:bg-white transition-colors flex items-center justify-center gap-3 rounded disabled:opacity-50">
            {submitting ? <><Loader2 className="animate-spin" size={20}/> REDIRECTION…</> : <>Continuer vers le paiement <ArrowRight size={20}/></>}
          </button>
          <div className="text-center text-xs text-white/40 flex items-center justify-center gap-2">
            <Shield size={12}/> Données protégées · L'équipe confirme le paiement et l'activation
          </div>
        </div>
      )}

      {/* ===== CONFIRMATION ===== */}
      {step === "done" && (
        <div className="max-w-2xl mx-auto px-6 py-14" data-testid="inscription-done">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-impact-gold/20 border-2 border-impact-gold mx-auto flex items-center justify-center mb-6">
              <Check size={40} className="text-impact-gold"/>
            </div>
            <h1 className="font-display font-black text-4xl mb-3">Bienvenue à l'Institut Impact Élite Martial</h1>
            <p className="text-white/80">{result?.paid
              ? "Votre paiement a été confirmé et votre inscription est activée. Un courriel de confirmation vous a été envoyé."
              : `Votre inscription a bien été reçue. ${result?.email_sent ? "Un courriel de confirmation vient de vous être envoyé." : "Notre équipe vous contactera par courriel."}`}</p>
          </div>

          {(result?.summary?.programme || progLabel) && (
          <div className="border border-white/10 rounded-xl p-5 bg-white/5 space-y-2 text-sm mt-8">
            <Recap k="Programme" v={result?.summary?.programme || progLabel}/>
            <Recap k={isParent ? "Enfant" : "Membre"} v={result?.summary?.full_name || form.full_name}/>
            <Recap k="Statut du paiement" v={result?.paid ? "Confirmé ✓" : "En cours d'activation avec l'équipe"}/>
          </div>
          )}

          <div className="mt-8">
            <h2 className="ticker text-impact-gold mb-3">VOS PROCHAINES ÉTAPES</h2>
            <ol className="space-y-2 text-sm text-white/80">
              {["Ouvrir votre courriel de confirmation", "Accéder à votre espace membre", "Vérifier vos informations", "Consulter votre horaire", "Ouvrir votre carnet ou votre parcours, si disponible"].map((s, i) => (
                <li key={i} className="flex gap-2.5"><span className="shrink-0 w-5 h-5 rounded-full bg-impact-gold/20 text-impact-gold text-[11px] font-bold flex items-center justify-center">{i + 1}</span>{s}</li>
              ))}
            </ol>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href="/membres" data-testid="done-espace-membre" className="flex-1 text-center bg-impact-gold text-black font-display font-bold py-3 rounded hover:bg-white transition-colors">Accéder à mon espace membre</a>
            <a href="/horaire" data-testid="done-horaire" className="flex-1 text-center border border-impact-gold/50 text-impact-gold font-semibold py-3 rounded hover:bg-impact-gold/10 transition-colors">Voir l'horaire</a>
          </div>
          <p className="text-center text-xs text-white/40 mt-6">Notre équipe complète actuellement la configuration de votre dossier. Vous recevrez toute confirmation utile par courriel. · (819) 527-9446</p>
        </div>
      )}

      <style>{`
        .input { width:100%; background:#ffffff10; border:1px solid #ffffff30; padding:0.7rem 0.9rem; color:white; outline:none; transition:border-color .2s; border-radius:6px; }
        .input:focus { border-color:#c9a961; }
        .input::placeholder { color:#ffffff50; }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3 border-l-2 border-impact-gold/60 pl-4">
      <h3 className="ticker text-impact-gold text-sm">{title}</h3>
      {children}
    </section>
  );
}
function Field({ label, children }) {
  return (<div><label className="text-xs text-white/60 block mb-1.5">{label}</label>{children}</div>);
}
function Recap({ k, v }) {
  return (<div className="flex justify-between gap-3"><span className="text-white/50">{k}</span><span className="text-white/90 font-medium text-right">{v}</span></div>);
}
function Consent({ id, checked, onChange, children }) {
  return (
    <label className="flex items-start gap-3 text-sm border border-impact-gold/30 bg-impact-gold/5 p-3 rounded cursor-pointer">
      <input data-testid={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 mt-0.5 flex-shrink-0"/>
      <span>{children}</span>
    </label>
  );
}
